/**
 * DefensibilityFlywheel - Horizontal Process Stepper
 * 
 * @description Implements the Upload → Map → Evidence → Claim → Audit pattern
 * as a horizontal layer that runs across all Operations modules.
 * 
 * This component provides:
 * - Visual progress indication through the flywheel
 * - Context-aware step highlighting
 * - Quick navigation between flywheel stages
 * - Completion status for each step
 * 
 * The flywheel is the "plumbing" of the platform - it runs inside every
 * Operations module, not as separate navigation items.
 * 
 * @component
 * @example
 * <DefensibilityFlywheel 
 *   currentStep="evidence"
 *   context="obligations"
 *   entityId="obl_123"
 * />
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { 
  Upload, 
  GitMerge, 
  Shield, 
  FileCheck, 
  CheckCircle2,
  ChevronRight,
  Info
} from 'lucide-react';

/**
 * Flywheel step configuration
 * Each step represents a stage in the defensibility process
 */
const FLYWHEEL_STEPS = [
  {
    id: 'upload',
    label: 'Upload',
    shortLabel: 'Upload',
    icon: Upload,
    description: 'Ingest data from ERPs, documents, and manual entry',
    color: 'emerald',
    actions: ['Import data', 'Connect ERP', 'Upload documents'],
  },
  {
    id: 'map',
    label: 'Map',
    shortLabel: 'Map',
    icon: GitMerge,
    description: 'Map data to regulatory requirements and frameworks',
    color: 'blue',
    actions: ['Framework mapping', 'Data classification', 'Gap analysis'],
  },
  {
    id: 'evidence',
    label: 'Evidence',
    shortLabel: 'Evidence',
    icon: Shield,
    description: 'Attach and validate supporting evidence',
    color: 'purple',
    actions: ['Attach evidence', 'Request validation', 'Link to claims'],
  },
  {
    id: 'claim',
    label: 'Claim',
    shortLabel: 'Claim',
    icon: FileCheck,
    description: 'Build defensible disclosure claims',
    color: 'amber',
    actions: ['Create claim', 'Add dependencies', 'Calculate strength'],
  },
  {
    id: 'audit',
    label: 'Audit',
    shortLabel: 'Audit',
    icon: CheckCircle2,
    description: 'Generate audit-ready packages',
    color: 'cyan',
    actions: ['Build audit pack', 'Export XBRL', 'Generate report'],
  },
];

/**
 * Color configurations for step states
 */
const STEP_COLORS = {
  emerald: {
    active: 'bg-emerald-500 text-white border-emerald-500',
    completed: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50',
    pending: 'bg-[#1F1F1F] text-[#6B6B6B] border-[#2F2F2F]',
    line: 'bg-emerald-500',
    lineIncomplete: 'bg-[#2F2F2F]',
  },
  blue: {
    active: 'bg-blue-500 text-white border-blue-500',
    completed: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
    pending: 'bg-[#1F1F1F] text-[#6B6B6B] border-[#2F2F2F]',
    line: 'bg-blue-500',
    lineIncomplete: 'bg-[#2F2F2F]',
  },
  purple: {
    active: 'bg-purple-500 text-white border-purple-500',
    completed: 'bg-purple-500/20 text-purple-400 border-purple-500/50',
    pending: 'bg-[#1F1F1F] text-[#6B6B6B] border-[#2F2F2F]',
    line: 'bg-purple-500',
    lineIncomplete: 'bg-[#2F2F2F]',
  },
  amber: {
    active: 'bg-amber-500 text-white border-amber-500',
    completed: 'bg-amber-500/20 text-amber-400 border-amber-500/50',
    pending: 'bg-[#1F1F1F] text-[#6B6B6B] border-[#2F2F2F]',
    line: 'bg-amber-500',
    lineIncomplete: 'bg-[#2F2F2F]',
  },
  cyan: {
    active: 'bg-cyan-500 text-white border-cyan-500',
    completed: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/50',
    pending: 'bg-[#1F1F1F] text-[#6B6B6B] border-[#2F2F2F]',
    line: 'bg-cyan-500',
    lineIncomplete: 'bg-[#2F2F2F]',
  },
};

/**
 * Individual step component
 */
const FlywheelStep = ({ 
  step, 
  index, 
  isActive, 
  isCompleted, 
  isLast,
  onClick,
  showDetails 
}) => {
  const Icon = step.icon;
  const colors = STEP_COLORS[step.color];
  
  // Determine step state
  const stepState = isActive ? 'active' : isCompleted ? 'completed' : 'pending';
  const stepClass = colors[stepState];
  
  return (
    <div className="flex items-center flex-1" data-testid={`flywheel-step-${step.id}`}>
      {/* Step Circle */}
      <button
        onClick={() => onClick(step)}
        className={`
          relative flex items-center justify-center w-10 h-10 rounded-full border-2 
          transition-all duration-200 hover:scale-105
          ${stepClass}
        `}
        title={step.description}
      >
        <Icon size={18} />
        
        {/* Step number badge */}
        <span className={`
          absolute -top-1 -right-1 w-4 h-4 rounded-full text-[10px] font-bold
          flex items-center justify-center
          ${isCompleted ? 'bg-emerald-500 text-white' : 'bg-[#2F2F2F] text-[#6B6B6B]'}
        `}>
          {isCompleted ? '✓' : index + 1}
        </span>
      </button>
      
      {/* Step Label */}
      <div className="ml-3 min-w-0">
        <p className={`text-sm font-medium ${isActive ? 'text-white' : 'text-[#A0A0A0]'}`}>
          {step.label}
        </p>
        {showDetails && (
          <p className="text-xs text-[#6B6B6B] truncate max-w-[120px]">
            {step.description}
          </p>
        )}
      </div>
      
      {/* Connector Line */}
      {!isLast && (
        <div className="flex-1 mx-4 h-0.5 rounded-full overflow-hidden">
          <div 
            className={`h-full transition-all duration-500 ${
              isCompleted ? colors.line : colors.lineIncomplete
            }`}
            style={{ width: isCompleted ? '100%' : '0%' }}
          />
        </div>
      )}
    </div>
  );
};

/**
 * Context-specific action suggestions
 */
const CONTEXT_SUGGESTIONS = {
  obligations: {
    upload: 'Import obligation data from ERP or upload evidence documents',
    map: 'Map obligations to CSDDD/ESRS framework requirements',
    evidence: 'Attach supporting evidence and request validation',
    claim: 'Build disclosure claims based on obligation completion',
    audit: 'Generate audit pack for completed obligations',
  },
  'value-chain': {
    upload: 'Import supplier data and risk assessments',
    map: 'Map suppliers to value chain tiers and risk categories',
    evidence: 'Attach audit reports and due diligence evidence',
    claim: 'Create supply chain due diligence claims',
    audit: 'Export value chain compliance report',
  },
  cases: {
    upload: 'Import case data and incident reports',
    map: 'Classify case type and link to impacted areas',
    evidence: 'Attach investigation findings and remediation proof',
    claim: 'Document remediation completion claims',
    audit: 'Generate case closure audit trail',
  },
  default: {
    upload: 'Upload relevant data and documents',
    map: 'Map to regulatory requirements',
    evidence: 'Attach supporting evidence',
    claim: 'Build defensible claims',
    audit: 'Generate audit-ready output',
  },
};

/**
 * Main DefensibilityFlywheel Component
 * 
 * @param {Object} props
 * @param {string} props.currentStep - Currently active step ID
 * @param {string} props.context - Module context (obligations, value-chain, cases)
 * @param {string} props.entityId - ID of the entity being processed
 * @param {Object} props.completedSteps - Object with step IDs as keys, boolean completion status
 * @param {Function} props.onStepClick - Callback when a step is clicked
 * @param {boolean} props.compact - Use compact view (icons only)
 * @param {boolean} props.showActions - Show action suggestions
 */
const DefensibilityFlywheel = ({
  currentStep = 'upload',
  context = 'default',
  entityId = null,
  completedSteps = {},
  onStepClick = () => {},
  compact = false,
  showActions = true,
}) => {
  const [expandedStep, setExpandedStep] = useState(null);
  
  // Find current step index
  const currentStepIndex = FLYWHEEL_STEPS.findIndex(s => s.id === currentStep);
  
  // Get context-specific suggestions
  const suggestions = CONTEXT_SUGGESTIONS[context] || CONTEXT_SUGGESTIONS.default;
  
  // Handle step click
  const handleStepClick = (step) => {
    setExpandedStep(expandedStep === step.id ? null : step.id);
    onStepClick(step);
  };
  
  // Determine if a step is completed
  const isStepCompleted = (stepId, index) => {
    if (completedSteps[stepId]) return true;
    return index < currentStepIndex;
  };
  
  if (compact) {
    // Compact view - just icons
    return (
      <div 
        className="flex items-center gap-1 p-2 bg-[#0F0F0F] border border-[#1F1F1F] rounded-lg"
        data-testid="defensibility-flywheel-compact"
      >
        {FLYWHEEL_STEPS.map((step, index) => {
          const Icon = step.icon;
          const isActive = step.id === currentStep;
          const isCompleted = isStepCompleted(step.id, index);
          const colors = STEP_COLORS[step.color];
          
          return (
            <React.Fragment key={step.id}>
              <button
                onClick={() => handleStepClick(step)}
                className={`
                  p-2 rounded-md transition-all
                  ${isActive ? colors.active : isCompleted ? colors.completed : colors.pending}
                `}
                title={`${step.label}: ${step.description}`}
              >
                <Icon size={16} />
              </button>
              {index < FLYWHEEL_STEPS.length - 1 && (
                <ChevronRight size={12} className="text-[#3F3F3F]" />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  }
  
  return (
    <div 
      className="bg-[#0F0F0F] border border-[#1F1F1F] rounded-xl p-4"
      data-testid="defensibility-flywheel"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-widest text-[#6B6B6B]">
            Defensibility Flywheel
          </span>
          <button className="text-[#6B6B6B] hover:text-white">
            <Info size={14} />
          </button>
        </div>
        {entityId && (
          <span className="text-xs text-[#6B6B6B] font-mono">
            {entityId}
          </span>
        )}
      </div>
      
      {/* Stepper */}
      <div className="flex items-center">
        {FLYWHEEL_STEPS.map((step, index) => (
          <FlywheelStep
            key={step.id}
            step={step}
            index={index}
            isActive={step.id === currentStep}
            isCompleted={isStepCompleted(step.id, index)}
            isLast={index === FLYWHEEL_STEPS.length - 1}
            onClick={handleStepClick}
            showDetails={!compact}
          />
        ))}
      </div>
      
      {/* Action Suggestions */}
      {showActions && (
        <div className="mt-4 pt-4 border-t border-[#1F1F1F]">
          <div className="flex items-start gap-3">
            <div className={`
              p-2 rounded-lg 
              ${STEP_COLORS[FLYWHEEL_STEPS[currentStepIndex]?.color || 'emerald'].completed}
            `}>
              {React.createElement(FLYWHEEL_STEPS[currentStepIndex]?.icon || Upload, { size: 16 })}
            </div>
            <div>
              <p className="text-sm text-white font-medium">
                Current: {FLYWHEEL_STEPS[currentStepIndex]?.label || 'Upload'}
              </p>
              <p className="text-xs text-[#6B6B6B] mt-1">
                {suggestions[currentStep] || suggestions.upload}
              </p>
              
              {/* Quick Actions */}
              <div className="flex gap-2 mt-3">
                {FLYWHEEL_STEPS[currentStepIndex]?.actions.slice(0, 3).map((action, i) => (
                  <button
                    key={i}
                    className="px-3 py-1.5 text-xs bg-[#1F1F1F] hover:bg-[#2F2F2F] text-[#A0A0A0] hover:text-white rounded-md transition-colors"
                    data-testid={`flywheel-action-${i}`}
                  >
                    {action}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

DefensibilityFlywheel.propTypes = {
  currentStep: PropTypes.oneOf(['upload', 'map', 'evidence', 'claim', 'audit']),
  context: PropTypes.oneOf(['obligations', 'value-chain', 'cases', 'default']),
  entityId: PropTypes.string,
  completedSteps: PropTypes.object,
  onStepClick: PropTypes.func,
  compact: PropTypes.bool,
  showActions: PropTypes.bool,
};

export default DefensibilityFlywheel;
